import { useParams } from "react-router-dom";
import { SmoothLink as Link } from "@/components/navigation";
import { SEOHead } from "@/components/seo";
import { Button } from "@/components/ui/button";

import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ChevronRight } from "lucide-react";

// Reusable treatment components
import { TreatmentHero } from "@/components/common/TreatmentHero";
import { PriceComparison } from "@/components/common/PriceComparison";
import { DoctorsList } from "@/components/common/DoctorsList";
import { HospitalsList } from "@/components/common/HospitalsList";
import { QuickInfoSidebar } from "@/components/common/QuickInfoSidebar";

// New enhanced components
import { TreatmentOverview } from "@/components/treatment/TreatmentOverview";
import { WhatsIncludedSection } from "@/components/treatment/WhatsIncludedSection";
import { FAQSection } from "@/components/treatment/FAQSection";
import { RelatedArticlesSection } from "@/components/treatment/RelatedArticlesSection";
import { AIAgentCard } from "@/components/ai";

// SEO components
import { BreadcrumbJsonLd, TreatmentServiceJsonLd, FAQJsonLd, MedicalWebPageSchema } from "@/components/seo";
import { Breadcrumbs } from "@/components/navigation/Breadcrumbs";

// TanStack Query hooks
import { useTreatmentDetail } from "@/hooks/useTreatmentDetail";
import { useFAQs } from "@/hooks/useFAQs";
import { useRelatedArticles } from "@/hooks/useRelatedArticles";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { COMPANY } from "@/config/company";

function TreatmentDetailSkeleton() {
  return (
    <div className="container mx-auto px-4">
      <Skeleton className="h-8 w-48 mb-8" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  );
}

export default function TreatmentDetail() {
  const { category, slug } = useParams();
  const { data: treatment, isLoading, error } = useTreatmentDetail(slug);
  const { data: faqs = [] } = useFAQs(treatment?.slug, treatment?.category);
  const { data: relatedArticles = [] } = useRelatedArticles(
    treatment?.id || "", 
    treatment?.category || ""
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">

        <main className="pt-24 pb-16">
          <TreatmentDetailSkeleton />
        </main>

      </div>
    );
  }

  if (error || !treatment) {
    return (
      <div className="min-h-screen bg-background">

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold text-secondary mb-4">Treatment Currently Unavailable</h1>
            <p className="text-muted-foreground mb-6">This treatment information is being updated. Explore our other world-class procedures!</p>
            <Link to="/treatments/">
              <Button>Browse All Treatments</Button>
            </Link>
          </div>
        </main>

      </div>
    );
  }

  const doctorCount = treatment.doctors?.length || 0;
  const hospitalCount = treatment.hospitals?.length || 0;
  const categoryLabel = treatment.category.replace("_", " ");

  // Prepare breadcrumb data for SEO
  const breadcrumbItems = [
    { name: "Home", url: `${COMPANY.website}/` },
    { name: "Treatments", url: `${COMPANY.website}/treatments` },
    { name: categoryLabel.charAt(0).toUpperCase() + categoryLabel.slice(1), url: `${COMPANY.website}/treatments/${treatment.category}` },
    { name: treatment.name, url: `${COMPANY.website}/treatments/${treatment.category}/${treatment.slug}` },
  ];

  // Prepare FAQ data for SEO
  const faqsForSchema = faqs.map(faq => ({
    question: faq.question,
    answer: faq.answer,
  }));

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* SEO Meta */}
            <SEOHead
        title={treatment?.name || "Treatment Details"}
        description={treatment?.description || "Explore affordable medical treatments in India"}
        canonicalPath={`/treatments/${treatment?.category}/${treatment?.slug}/`}
        ogType="article"
      />

      {/* Structured Data for SEO/LLM */}
      <BreadcrumbJsonLd items={breadcrumbItems} />
      <MedicalWebPageSchema
        name={treatment.name}
        description={treatment.description || `Affordable ${treatment.name} in India`}
        url={`${COMPANY.website}/treatments/${treatment.category}/${treatment.slug}/`}
        medicalAudience={["Patient"]}
        about={{
          name: treatment.name,
          code: treatment.procedure_code || undefined,
        }}
        specialty={categoryLabel.charAt(0).toUpperCase() + categoryLabel.slice(1)}
        lastReviewed={treatment.updated_at || treatment.created_at}
      />
      <TreatmentServiceJsonLd
        name={treatment.name}
        description={treatment.description || `Affordable ${treatment.name} in India`}
        category={treatment.category}
        priceIndia={treatment.price_india_min && treatment.price_india_max ? {
          min: treatment.price_india_min,
          max: treatment.price_india_max,
        } : undefined}
        priceUSA={treatment.price_usa_min && treatment.price_usa_max ? {
          min: treatment.price_usa_min,
          max: treatment.price_usa_max,
        } : undefined}
        url={`${COMPANY.website}/treatments/${treatment.category}/${treatment.slug}`}
        imageUrl={getOptimizedImageUrl(treatment.image_url, 1200) || undefined}
      />
      {faqsForSchema.length > 0 && <FAQJsonLd faqs={faqsForSchema} />}


      
      <main className="pt-20 sm:pt-24 pb-12 sm:pb-16">
        <div className="container mx-auto px-3 sm:px-4 max-w-7xl">
          {/* Breadcrumb Navigation */}
          <Breadcrumbs items={breadcrumbItems.map(item => ({
            name: item.name,
            url: item.url.replace(COMPANY.website, '')
          }))} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8 min-w-0">
              {/* Hero Image & Title */}
              <TreatmentHero treatment={treatment} />

              {/* AI Assistant CTA - Mobile Prominent */}
              <div className="lg:hidden">
                <AIAgentCard 
                  context={treatment.name}
                  title={`Talk to Dr. Maya About ${treatment.name}`}
                  description="Get personalized answers, cost estimates, and help booking your consultation."
                  quickPrompts={[
                    "Get a free quote",
                    "Am I a good candidate?",
                    "Book consultation"
                  ]}
                />
              </div>

              {/* Treatment Overview with Description */}
              <TreatmentOverview 
                treatment={treatment}
                doctorCount={doctorCount}
                hospitalCount={hospitalCount}
              />

              {/* What's Included - Enhanced with Categories & Slides */}
              <WhatsIncludedSection items={treatment.included_items} />

              {/* Price Comparison Table */}
              <PriceComparison treatment={treatment} />

              {/* Doctors Section - Grouped by type */}
              <DoctorsList doctors={treatment.doctors || []} />

              {/* Hospitals Section */}
              <HospitalsList hospitals={treatment.hospitals || []} />

              {/* FAQ Section */}
              <FAQSection 
                faqs={faqs} 
                treatmentName={treatment.name}
                related_trends={treatment.related_trends}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6 min-w-0">
              {/* Quick Info Card */}
              <QuickInfoSidebar 
                treatment={treatment}
                doctorCount={doctorCount}
                hospitalCount={hospitalCount}
              />

              {/* AI Assistant CTA - Desktop */}
              <div className="hidden lg:block">
                <AIAgentCard 
                  context={treatment.name}
                  title={`Talk to Dr. Maya About ${treatment.name}`}
                  description="Get personalized answers, cost estimates, and help booking your consultation."
                  quickPrompts={[
                    "Get a free quote",
                    "Am I a good candidate?",
                    "Book consultation"
                  ]}
                />
              </div>

              {/* Related Articles in Sidebar */}
              {relatedArticles.length > 0 && (
                <RelatedArticlesSection 
                  articles={relatedArticles}
                  treatmentCategory={treatment.category}
                />
              )}
            </div>
          </div>
        </div>
      </main>


    </div>
  );
}
